import React, { useEffect, useState } from 'react';
import { AppContext } from '../../../App';
import { CosmosText, CosmosTooltip } from '../../../cosmos/web-scoped/react';
import { Placeholder } from '../../Placeholder';
import { getTrans } from '../../../Utils/translate';
import { Comparison } from '../../../Utils/comparison';
import get from 'lodash/get';
import find from 'lodash/find';
import CountUp from 'react-countup';
import { motion } from 'framer-motion';

import './ProfileStats.scss';

/**
 * renders the compared stats
 *
 * @param athleteLeft
 * @param athleteRight
 * @param rules
 * @returns {JSX.Element}
 * @constructor
 * @example
 *
 * the parameter rules extends the default rules, e.g.
 * {age: { operator: CompareOperator.gt }}
 *
 */
 export const ProfileStats = ({athleteLeft, athleteRight, rules = {}, eventType}) => {
  const context = React.useContext(AppContext);
  const comparison = new Comparison();
  comparison.setRules(rules);

  // default configuration
  let statTypes = [
    { key: 'age', label: 'age' },
    { condition: { stat_type: 'short_fact_1' }, path: 'career_stats', key: 'fact', label: 'textShortFact1'},
    { condition: { stat_type: 'short_fact_2' }, path: 'career_stats', key: 'fact', label: 'textShortFact2'},
  ]
  // configurations by event type
  if (['cliff'].includes(eventType)) {
    statTypes = [
      { key: 'age', label: 'age' }, // Age (text)
      { path: 'series_rankings', key: 'rank', label: 'seriesStanding'}, // Series Standing (text)
      { condition: { stat_type: 'DivesSince' }, path: 'career_stats', key: 'fact', label: 'divesSince'}, // Dives Since (text)
      { condition: { stat_type: 'Cliff Diving World Series' }, path: 'career_stats', key: 'starts', label: 'starts', template: 'bar', class: 'first-bar' }, // Start (Bar)
      { condition: { stat_type: 'Cliff Diving World Series' }, path: 'career_stats', key: 'top5', label: 'podiums', template: 'bar'}, // Podiums (Bar)
      { condition: { stat_type: 'Cliff Diving World Series' }, path: 'career_stats', key: 'wins', label: 'wins', template: 'bar'}, // Wins (Bar)
    ];
  } else if (['cx_slopestyle'].includes(eventType)) {
    statTypes = [
      { key: 'age', label: 'age' }, // Age (text)
      { path: 'series_rankings', key: 'rank', label: 'seriesStanding'}, // Series Standing (text)
      { path: 'series_rankings', key: 'points', label: 'points'}, // Series Points (text)
      { condition: { stat_type: 'Crankworx' }, path: 'career_stats', key: 'starts', label: 'starts', template: 'bar', class: 'first-bar'}, // Start (Bar)
      { condition: { stat_type: 'Crankworx' }, path: 'career_stats', key: 'top5', label: 'podiums', template: 'bar'}, // Podiums (Bar)
      { condition: { stat_type: 'Crankworx' }, path: 'career_stats', key: 'wins', label: 'wins', template: 'bar'}, // Wins (Bar)
    ];
  } else if (['mtb'].includes(eventType)) {
    statTypes = [
        { key: 'age', label: 'age' }, // Age (text)
        { path: 'series_rankings', key: 'rank', label: 'seriesStanding'}, // World Cup Standing (text)
        { path: 'series_rankings', key: 'points', label: 'points'}, // World Cup Points (Text)
        { condition: { stat_type: 'World Cup' }, path: 'career_stats', key: 'starts', label: 'starts', template: 'bar', class: 'first-bar'}, // Start (Bar)
        { condition: { stat_type: 'World Cup' }, path: 'career_stats', key: 'top5', label: 'podiums', template: 'bar'}, // Podiums (Bar)
        { condition: { stat_type: 'World Cup' }, path: 'career_stats', key: 'wins', label: 'wins', template: 'bar'}, // Wins (Bar)
    ];
  } else if (['rampage'].includes(eventType)) {
    statTypes = [
        { key: 'age', label: 'age' },
        { condition: { stat_type: 'FirstRampage' }, path: 'career_stats', key: 'fact', label: 'rbes.profile.stats.firstrampage:First Rampage'},
        { condition: { stat_type: 'HighestRampageScore' }, path: 'career_stats', key: 'fact', label: 'rbes.profile.stats.highscore:Career Highscore'},
        { condition: { stat_type: 'Red Bull Rampage' }, path: 'career_stats', key: 'starts', label: 'starts', template: 'bar', class: 'first-bar'},
        { condition: { stat_type: 'Red Bull Rampage' }, path: 'career_stats', key: 'top5', label: 'podiums', template: 'bar'},
        { condition: { stat_type: 'Red Bull Rampage' }, path: 'career_stats', key: 'wins', label: 'wins', template: 'bar'},
    ];
  } else if (['enduro'].includes(eventType)) {
      statTypes = [
          {key: 'age', label: 'age'},
          {path: 'series_rankings', key: 'rank', label: 'seriesStanding'},
          {path: 'series_rankings', key: 'points', label: 'points'},
          {
              condition: {stat_type: 'Overall'},
              path: 'career_stats',
              key: 'starts',
              label: 'starts',
              template: 'bar',
              class: 'first-bar'
          },
          {condition: {stat_type: 'Overall'}, path: 'career_stats', key: 'top5', label: 'podiums', template: 'bar'},
          {condition: {stat_type: 'Overall'}, path: 'career_stats', key: 'wins', label: 'wins', template: 'bar'},
      ];
  } else if (['urbandhi'].includes(eventType)) {
      statTypes = [
          { key: 'age', label: 'age' },
          { path: 'series_rankings', key: 'rank', label: 'seriesStanding'},
          { path: 'series_rankings', key: 'points', label: 'points'},
          { condition: { stat_type: 'Overall' }, path: 'career_stats', key: 'starts', label: 'starts', template: 'bar', class: 'first-bar'},
          { condition: { stat_type: 'Overall' }, path: 'career_stats', key: 'top5', label: 'podiums', template: 'bar'},
          { condition: { stat_type: 'Overall' }, path: 'career_stats', key: 'wins', label: 'wins', template: 'bar'},
      ];
  }

  const findLabelByKey = (key) => {
    return getTrans(key) || getTrans(context.lang[`${key}`]) || key;
  };

  const findStatByType = (type, athlete) => {
    let obj = athlete;
    // use path if given
    obj = type.path ? get(obj, type.path) : obj;
    // use condition if given, results in an array
    obj = type.condition && Array.isArray(obj) ? find(obj, type.condition) : obj;
    // value is still an array? use first
    obj = Array.isArray(obj) ? obj[0] : obj;
    // get value by key, with default value 'false' (bool)
    let value = obj && obj.hasOwnProperty(type.key) && obj[type.key];
    // return translation if existing
    return getTrans(value) || value;
  };

  return (
    <div className="stats">
      {statTypes.map((type) => {
        const stat = {
          left: findStatByType(type, athleteLeft),
          label: findLabelByKey(type.label),
          right: findStatByType(type, athleteRight)
        }
        stat.firstWins = comparison.firstValueWins(stat.left, stat.right, type.key);

        return (
          <div key={type.label}>
            {type.class === 'first-bar' &&
              <div key={`${type.label}-separator`} className="separator" />
            }
            <div className={`row-stat template-${type.template || 'normal'} ${type.class || ''}`}>
              <div className={`row-stat__text row-stat__text-left ${stat.firstWins === true ? 'wins' : ''}`}>
                <ProfileStat stat={stat} side="left" template={type.template} />
              </div>
              <CosmosText
                class={`row-stat__text row-stat__text-label`}>
                {stat.label}
              </CosmosText>
              <div className={`row-stat__text row-stat__text-right ${stat.firstWins === false ? 'wins' : ''}`}>
                <ProfileStat stat={stat} side="right" template={type.template} />
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
};

const ProfileStat = ({stat, side = 'left', template = 'text'}) => {
  const [max, setMax] = useState(-1);
  const modulus = 5;

  useEffect(() => {
    const _max = Math.max(stat.left || 0, stat.right || 0);
    const rest = _max % modulus;
    let result = rest > 0 ? _max - rest + modulus : _max - rest;
    setMax(result);
  }, [stat])

  if (template === 'text') {
      if (stat[side]) {
        return (
          <ProfileStatText value={stat[side]}/>
        )
      } else {
        return (
          <>
            <Placeholder className="row-stat__text row-stat__text-right me-3" style={{ display: 'inline-block' }} maxWidth="30px" height="6px" />
            <Placeholder style={{ display: 'inline-block' }} maxWidth="30px" height="6px" />
          </>
        )
      }
  } else if (template === 'bar') {
    return (
      <CosmosTooltip content={stat[side]}>
        <ProfileStatBar value={stat[side]} max={max} side={side} />
      </CosmosTooltip>
    );
  }
}

const ProfileStatText = ({value}) => {
  const context = React.useContext(AppContext);
  const animationConfig = context.config.animation;
  // interpret numbers in string format to use count up animation
  value = !isNaN(value) ? Number.parseFloat(value) : value;
  return (
    <CosmosText class="row-stat__text__label-value">
      {typeof value === 'number'
        ? <CountUp start={0} end={value} duration={animationConfig.duration}/>
        : value
      }
    </CosmosText>
  );
}

const ProfileStatBar = ({value, max, side}) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const _width = Math.floor(value / max * 100);
    setWidth(_width);
  }, [value, max])

  return (
    <div className={`container-bar container-bar-side-${side}`}>
      <div className="progressbar" style={{ direction: side === 'left' && 'rtl' }}>
        <motion.div
          animate={{ width: `${width || 0}%` }}
          transition={{ duration: (width || 0) / 90 }}
        />
      </div>
      <CosmosText
        class="label-max"
        size="x-small"
        kind="subtle">
        {max}
      </CosmosText>
    </div>
  );
}